/**
 * Generic per-endpoint form. Right pane of the explorer.
 *
 * State model:
 *   - `pathParams` — keyed by param name. Backed by localStorage.
 *   - `bodyText` — raw JSON string. We do NOT validate on edit (the
 *     emulator backend + Gateway will report errors via the response
 *     viewer — that IS the dev tool's job, to show real wire errors).
 *
 * Special cases:
 *   - `renderOverride=true` (storage_upload, storage_download) → show
 *     a "Day 3 — special handling" placeholder card, no Send button.
 *   - `bodyShape=none` (GETs, DELETE) → no body textarea.
 *   - `signedSpec !== null` → show "auto-signed" hint above the body.
 *
 * Reset to defaults:
 *   - Clears localStorage for this endpoint + reverts pathParams/bodyText
 *     to catalog defaults. Single button at the bottom.
 */
import { useEffect, useMemo, useState } from 'react';
import type { EndpointSpec, ProxyResult } from '@/lib/types';
import { callProxy } from '@/lib/api';
import { clearState, loadState, saveState } from '@/lib/localStorage';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

interface Props {
  endpoint: EndpointSpec;
  onResult: (r: ProxyResult) => void;
}

function defaultBodyText(ep: EndpointSpec): string {
  if (ep.defaultBody === null || ep.defaultBody === undefined) return '';
  try {
    return JSON.stringify(ep.defaultBody, null, 2);
  } catch {
    return '';
  }
}

function defaultPathParams(ep: EndpointSpec): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of ep.pathParams) out[p.name] = p.defaultValue;
  return out;
}

export function EndpointForm({ endpoint, onResult }: Props) {
  const initial = useMemo(() => {
    const persisted = loadState(endpoint.id);
    if (persisted) {
      return { pathParams: persisted.pathParams, bodyText: persisted.bodyText };
    }
    return { pathParams: defaultPathParams(endpoint), bodyText: defaultBodyText(endpoint) };
  }, [endpoint]);

  const [pathParams, setPathParams] = useState<Record<string, string>>(initial.pathParams);
  const [bodyText, setBodyText] = useState<string>(initial.bodyText);
  const [sending, setSending] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  // Re-sync when endpoint changes (selecting a different one).
  useEffect(() => {
    setPathParams(initial.pathParams);
    setBodyText(initial.bodyText);
    setParseError(null);
  }, [initial]);

  // Persist on edit (debounced via React's natural batching — no setTimeout
  // needed for the volume we're talking about).
  useEffect(() => {
    saveState(endpoint.id, { pathParams, bodyText });
  }, [endpoint.id, pathParams, bodyText]);

  if (endpoint.renderOverride) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <span className="font-mono text-xs mr-2 text-slate-500">{endpoint.method}</span>
            <span className="font-mono">{endpoint.pathTemplate}</span>
          </CardTitle>
          <CardDescription>{endpoint.summary}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
            <div className="font-semibold mb-1">Day 2 limitation</div>
            <p>{endpoint.notes ?? 'Generic form skipped — special handling required.'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  async function handleSend() {
    setSending(true);
    setParseError(null);
    let parsedBody: unknown;
    if (endpoint.bodyShape === 'json' && bodyText.trim() !== '') {
      try {
        parsedBody = JSON.parse(bodyText);
      } catch (e) {
        setParseError(`JSON parse error: ${(e as Error).message}`);
        setSending(false);
        return;
      }
    }
    try {
      const result = await callProxy(endpoint.id, {
        pathParams,
        ...(parsedBody !== undefined ? { body: parsedBody } : {}),
      });
      onResult(result);
    } catch (e) {
      onResult({
        proxy_status: 'gateway_unreachable',
        endpoint_id: endpoint.id,
        gateway_url: '(emulator backend unreachable)',
        gateway_method: endpoint.method,
        error_kind: 'network',
        error_message: (e as Error).message,
        elapsed_ms: 0,
      });
    } finally {
      setSending(false);
    }
  }

  function handleReset() {
    clearState(endpoint.id);
    setPathParams(defaultPathParams(endpoint));
    setBodyText(defaultBodyText(endpoint));
    setParseError(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
            {endpoint.method}
          </span>
          <span className="font-mono">{endpoint.pathTemplate}</span>
          {endpoint.signedSpec !== null && (
            <Badge variant="info" title="Body is canonicalized + ed25519-signed by emulator">
              auto-signed: {endpoint.signedSpec}
            </Badge>
          )}
          {endpoint.auth === 'op+issuer' && (
            <Badge variant="warning" title="Requires X-Issuer-Token">
              issuer-token
            </Badge>
          )}
          {endpoint.auth === 'none' && <Badge variant="muted">no auth</Badge>}
        </CardTitle>
        <CardDescription>{endpoint.summary}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {endpoint.notes && (
          <div className="rounded-md bg-sky-50 border border-sky-200 p-3 text-xs text-sky-900">
            {endpoint.notes}
          </div>
        )}
        {endpoint.pathParams.length > 0 && (
          <div className="space-y-3">
            <Label>Path parameters</Label>
            {endpoint.pathParams.map((p) => (
              <div key={p.name} className="space-y-1">
                <div className="flex items-baseline justify-between">
                  <code className="text-xs font-mono text-slate-700">{`{${p.name}}`}</code>
                  <span className="text-[11px] text-slate-500">{p.hint}</span>
                </div>
                <Input
                  value={pathParams[p.name] ?? ''}
                  onChange={(e) => setPathParams((prev) => ({ ...prev, [p.name]: e.target.value }))}
                  placeholder={p.defaultValue}
                />
              </div>
            ))}
          </div>
        )}
        {endpoint.bodyShape === 'json' && (
          <div className="space-y-1">
            <div className="flex items-baseline justify-between">
              <Label>Request body (JSON)</Label>
              {endpoint.signedSpec !== null && (
                <span className="text-[11px] text-slate-500">
                  signatures field is auto-filled by emulator
                </span>
              )}
            </div>
            <Textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={endpoint.signedSpec === 'contract' ? 22 : 14}
              placeholder="{}"
            />
            {parseError && <p className="text-xs text-err mt-1">{parseError}</p>}
          </div>
        )}
        {endpoint.bodyShape === 'none' && (
          <p className="text-xs text-slate-500 italic">No request body for this endpoint.</p>
        )}
      </CardContent>
      <CardFooter className="flex justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={handleReset} disabled={sending}>
          Reset to defaults
        </Button>
        <Button onClick={() => void handleSend()} disabled={sending}>
          {sending ? 'Sending…' : `Send ${endpoint.method}`}
        </Button>
      </CardFooter>
    </Card>
  );
}
